import { useState, useEffect, useCallback } from 'react';
import { trafficRuleAPI } from '../services/api';

const styles = {
  container: { width: '100%' },
  header: { marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  title: { fontSize: '24px', fontWeight: '700', color: '#1e293b', margin: 0 },
  subtitle: { fontSize: '13px', color: '#64748b', marginTop: '4px' },
  card: { backgroundColor: '#ffffff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' },
  toolbar: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '16px', borderBottom: '1px solid #e2e8f0' },
  addButton: { padding: '10px 20px', backgroundColor: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
  td: { padding: '16px', borderBottom: '1px solid #f1f5f9', fontSize: '14px', color: '#1e293b', verticalAlign: 'top' },
  orderBadge: { display: 'inline-block', width: '28px', height: '28px', borderRadius: '50%', backgroundColor: '#eef2ff', color: '#6366f1', fontSize: '12px', fontWeight: '600', textAlign: 'center', lineHeight: '28px' },
  statusBadge: { padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', cursor: 'pointer' },
  activeStatus: { backgroundColor: '#dcfce7', color: '#166534' },
  inactiveStatus: { backgroundColor: '#fee2e2', color: '#991b1b' },
  actionButtons: { display: 'flex', gap: '8px' },
  iconButton: { padding: '8px', border: 'none', borderRadius: '6px', backgroundColor: 'transparent', cursor: 'pointer', color: '#64748b' },
  emptyState: { textAlign: 'center', padding: '60px 20px', color: '#64748b' },
  modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalContent: { backgroundColor: '#fff', borderRadius: '16px', width: '90%', maxWidth: '600px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  modalHeader: { padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  modalTitle: { fontSize: '18px', fontWeight: '600', color: '#1e293b' },
  modalBody: { padding: '24px', overflowY: 'auto', flex: 1 },
  modalFooter: { padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '12px' },
  formGroup: { marginBottom: '16px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '500', color: '#475569', marginBottom: '6px' },
  input: { width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', resize: 'vertical', minHeight: '80px', fontFamily: 'inherit' },
  button: { padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', border: 'none' },
  primaryButton: { backgroundColor: '#4f46e5', color: '#fff' },
  secondaryButton: { backgroundColor: '#f1f5f9', color: '#475569' },
  errorBox: { padding: '12px 16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', marginBottom: '16px', fontSize: '14px' },
  checkboxRow: { display: 'flex', alignItems: 'center', gap: '8px' },
};

const TrafficRules = () => {
  const [rules, setRules] = useState([]);
  const [states, setStates] = useState([]); // [{ id, stateName }]
  const [filterStateId, setFilterStateId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ offense: '', penalty: '', stateId: '', sortOrder: 0, isActive: true });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchRules = useCallback(async () => {
    try {
      setLoading(true);
      const params = filterStateId ? { stateId: filterStateId } : {};
      const res = await trafficRuleAPI.getAll(params);
      setRules(res.data.data || []);
    } catch { setError('Failed to fetch traffic rules'); }
    finally { setLoading(false); }
  }, [filterStateId]);

  const fetchStates = useCallback(async () => {
    try {
      const res = await trafficRuleAPI.getStates();
      setStates(res.data.data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchRules(); }, [fetchRules]);
  useEffect(() => { fetchStates(); }, [fetchStates]);

  const openCreate = () => {
    setEditing(null);
    setFormData({ offense: '', penalty: '', stateId: filterStateId || '', sortOrder: rules.length + 1, isActive: true });
    setFormError(''); setShowModal(true);
  };

  const openEdit = (rule) => {
    setEditing(rule);
    setFormData({ offense: rule.offense, penalty: rule.penalty, stateId: rule.stateId ? String(rule.stateId) : '', sortOrder: rule.sortOrder, isActive: rule.isActive });
    setFormError(''); setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.offense || !formData.penalty) { setFormError('Offense and penalty are required'); return; }
    try {
      setSaving(true);
      if (editing) await trafficRuleAPI.update(editing.id, formData);
      else await trafficRuleAPI.create(formData);
      setShowModal(false);
      fetchRules();
    } catch (e) { setFormError(e.response?.data?.error || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleToggle = async (rule) => {
    try { await trafficRuleAPI.toggleActive(rule.id); fetchRules(); }
    catch { setError('Failed to toggle'); }
  };

  const handleDelete = async (rule) => {
    if (!window.confirm(`Delete this rule?`)) return;
    try { await trafficRuleAPI.delete(rule.id); fetchRules(); }
    catch { setError('Failed to delete'); }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Traffic Rules & Penalties</h1>
          <p style={styles.subtitle}>Manage traffic rules shown on the website's /traffic-rules page</p>
        </div>
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}

      <div style={styles.card}>
        <div style={{ ...styles.toolbar, justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>Filter by state:</label>
            <select
              value={filterStateId}
              onChange={(e) => setFilterStateId(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', backgroundColor: '#fff', minWidth: '180px' }}
            >
              <option value="">All (including pan-India)</option>
              {states.map((s) => <option key={s.id} value={s.id}>{s.stateName}</option>)}
            </select>
          </div>
          <button style={styles.addButton} onClick={openCreate}>
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M12 4v16m-8-8h16" /></svg>
            Add Rule
          </button>
        </div>

        {loading ? (
          <div style={styles.emptyState}>Loading...</div>
        ) : rules.length === 0 ? (
          <div style={styles.emptyState}>No traffic rules yet. Click "Add Rule" to create one.</div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, width: '60px' }}>Order</th>
                <th style={styles.th}>Offense</th>
                <th style={{ ...styles.th, width: '240px' }}>Penalty</th>
                <th style={{ ...styles.th, width: '120px' }}>State</th>
                <th style={{ ...styles.th, width: '100px' }}>Status</th>
                <th style={{ ...styles.th, width: '100px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((rule) => (
                <tr key={rule.id}>
                  <td style={styles.td}><span style={styles.orderBadge}>{rule.sortOrder}</span></td>
                  <td style={{ ...styles.td, lineHeight: '1.5' }}>{rule.offense}</td>
                  <td style={{ ...styles.td, fontWeight: '600', lineHeight: '1.5' }}>{rule.penalty}</td>
                  <td style={styles.td}>
                    {rule.state?.stateName ? (
                      <span style={{ fontSize: '12px', fontWeight: '600', color: '#6366f1', background: '#eef2ff', padding: '3px 8px', borderRadius: '6px' }}>{rule.state.stateName}</span>
                    ) : (
                      <span style={{ fontSize: '12px', color: '#64748b' }}>All India</span>
                    )}
                  </td>
                  <td style={styles.td}>
                    <span
                      style={{ ...styles.statusBadge, ...(rule.isActive ? styles.activeStatus : styles.inactiveStatus) }}
                      onClick={() => handleToggle(rule)}
                      title="Click to toggle"
                    >
                      {rule.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.actionButtons}>
                      <button style={styles.iconButton} onClick={() => openEdit(rule)} title="Edit">
                        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                      </button>
                      <button style={{ ...styles.iconButton, color: '#dc2626' }} onClick={() => handleDelete(rule)} title="Delete">
                        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div style={styles.modal} onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>{editing ? 'Edit Traffic Rule' : 'Add Traffic Rule'}</h2>
              <button style={styles.iconButton} onClick={() => setShowModal(false)}>
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div style={styles.modalBody}>
              {formError && <div style={styles.errorBox}>{formError}</div>}

              <div style={styles.formGroup}>
                <label style={styles.label}>Offense *</label>
                <textarea style={styles.textarea} value={formData.offense} onChange={(e) => setFormData({ ...formData, offense: e.target.value })} placeholder="e.g. Driving without License (or, person expired/suspended DL)" />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Penalty *</label>
                <textarea style={styles.textarea} value={formData.penalty} onChange={(e) => setFormData({ ...formData, penalty: e.target.value })} placeholder="e.g. ₹ 5,000 / ₹ 10,000 (and/or community service)" />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>State</label>
                <select
                  style={styles.input}
                  value={formData.stateId}
                  onChange={(e) => setFormData({ ...formData, stateId: e.target.value })}
                >
                  <option value="">All India (pan-India rule)</option>
                  {states.map((s) => <option key={s.id} value={s.id}>{s.stateName}</option>)}
                </select>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                  Select a state for state-specific fines, or leave empty for rules that apply nationwide.
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Sort Order</label>
                <input style={styles.input} type="number" value={formData.sortOrder} onChange={(e) => setFormData({ ...formData, sortOrder: Number(e.target.value) })} />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.checkboxRow}>
                  <input type="checkbox" checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} />
                  <span style={{ fontSize: '14px' }}>Active (visible on the website)</span>
                </label>
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => setShowModal(false)} disabled={saving}>Cancel</button>
              <button style={{ ...styles.button, ...styles.primaryButton, opacity: saving ? 0.6 : 1 }} onClick={handleSubmit} disabled={saving}>
                {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrafficRules;
